import { getSurveyAnswers } from "src/hooks/survey/answers";
import { updateCurrentAnswers } from "@utils/redux/slices/currentAnswers";
import { oralsHealthRiskOptions } from "src/pages/survey/currentHealthRiskMedical";
import { addSurveyAnswers } from "@utils/redux/slices/surveyAnswers";
import { getPageSetSurvey } from "./util";
import { ActionHandlerParams } from "..";

type HealthRiskOption = (typeof oralsHealthRiskOptions)[number];

export const seedCurrentHealthRiskMedicalFromDiabetesType = async ({
  store,
  pageSet,
  page,
}: ActionHandlerParams): Promise<void> => {
  const survey = getPageSetSurvey(pageSet, page);
  const surveyAnswers = getSurveyAnswers();
  const diabetesType = surveyAnswers.diabetesType?.[0];
  const currentHealthRiskValues = (surveyAnswers.currentHealthRiskMedical ||
    []) as HealthRiskOption[];

  // Map diabetesType to currentHealthRiskMedical value
  const healthRiskMapping: Partial<
    Record<typeof diabetesType, HealthRiskOption>
  > = {
    type1: "type1Diabetes",
    type2: "type2Diabetes",
  };

  // Create a new set of health risks based on current values
  let updatedHealthRisks: HealthRiskOption[] = [];

  // If diabetesType has a value, ensure the corresponding risk is included
  if (diabetesType && healthRiskMapping[diabetesType]) {
    const mappedRisk = healthRiskMapping[diabetesType];

    // Start with existing values, but filter out any diabetes-related options
    const nonDiabetesRisks = currentHealthRiskValues.filter(
      (risk) => risk !== "type1Diabetes" && risk !== "type2Diabetes"
    );

    // Add the mapped risk from diabetesType
    updatedHealthRisks = [...nonDiabetesRisks, mappedRisk];
  } else {
    // If no diabetesType, filter out diabetes-related risks
    updatedHealthRisks = currentHealthRiskValues.filter(
      (risk) => risk !== "type1Diabetes" && risk !== "type2Diabetes"
    );
  }

  // Update the survey answers with the filtered health risks
  store.dispatch(
    addSurveyAnswers({
      currentHealthRiskMedical: updatedHealthRisks,
    })
  );
  store.dispatch(
    updateCurrentAnswers({
      namespace: survey.surveyNameSpace,
      answers: {
        currentHealthRiskMedical: updatedHealthRisks,
      },
    })
  );
};

export const seedCurrentHealthRiskMedicalFromMetabolicDiagnoses = async ({
  store,
  pageSet,
  page,
}: ActionHandlerParams): Promise<void> => {
  const survey = getPageSetSurvey(pageSet, page);
  const surveyAnswers = getSurveyAnswers();
  const metabolicDiagnoses = (surveyAnswers.metabolicDiagnoses ||
    []) as string[];
  const currentHealthRiskValues = (surveyAnswers.currentHealthRiskMedical ||
    []) as HealthRiskOption[];

  // Map metabolicDiagnoses values to currentHealthRiskMedical values
  // Only type1Diabetes and type2Diabetes map directly
  const healthRiskMapping: Partial<Record<string, HealthRiskOption>> = {
    type1Diabetes: "type1Diabetes",
    type2Diabetes: "type2Diabetes",
  };

  // Create a new set of health risks based on current values
  let updatedHealthRisks: HealthRiskOption[] = [];

  // Get the mapped risks from metabolicDiagnoses
  const mappedRisks = metabolicDiagnoses
    .map((diagnosis) => healthRiskMapping[diagnosis])
    .filter((risk): risk is HealthRiskOption => risk !== undefined);

  // Start with existing values, but filter out any diabetes-related options
  const nonDiabetesRisks = currentHealthRiskValues.filter(
    (risk) => risk !== "type1Diabetes" && risk !== "type2Diabetes"
  );

  // Add the mapped risks from metabolicDiagnoses (empty array expands to nothing)
  updatedHealthRisks = [...nonDiabetesRisks, ...mappedRisks];

  // Update the survey answers with the filtered health risks
  store.dispatch(
    addSurveyAnswers({
      currentHealthRiskMedical: updatedHealthRisks,
    })
  );
  store.dispatch(
    updateCurrentAnswers({
      namespace: survey.surveyNameSpace,
      answers: {
        currentHealthRiskMedical: updatedHealthRisks,
      },
    })
  );
};
